import { useRef, useEffect } from 'react';
import { useCallbackOne } from 'use-memo-one';
import { BridgeContextObject, type BridgeContextInput, type ChangeListener } from './BridgeContextObject';
import type { BridgeContext, BridgeServices, BridgeControls } from './types';

export { BRIDGE_CONTEXT_EVENTS } from './BridgeContextObject';

export const useBridgeContext = (
	props: BridgeContextInput,
	getServices: () => BridgeServices,
): {
	destroy: () => void;
	getBridgeContext: () => BridgeContext;
	onChange: (listener: ChangeListener) => () => void;
	setBridgeControl: (controls: BridgeControls) => void;
} => {
	// Created once for the lifetime of the component.
	// BridgeContextObject owns all memoization and change notification internally.
	const contextObjectRef = useRef<BridgeContextObject | null>(null);
	if (contextObjectRef.current === null) {
		contextObjectRef.current = new BridgeContextObject(props, getServices);
	}

	// Update after every commit. Bridge calls arrive via postRobot (async message events)
	// so the context will always be current before any bridge method is invoked.
	useEffect(() => {
		contextObjectRef.current!.update(props);
	});

	const getBridgeContext = useCallbackOne(() => contextObjectRef.current!.get(), []);
	const destroy = useCallbackOne(() => contextObjectRef.current!.destroy(), []);
	const onChange = useCallbackOne(
		(listener: ChangeListener) => contextObjectRef.current!.onChange(listener),
		[],
	);
	const setBridgeControl = useCallbackOne(
		(controls: BridgeControls) => contextObjectRef.current!.setBridgeControl(controls),
		[],
	);

	return { getBridgeContext, destroy, onChange, setBridgeControl };
};
